import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, AlertTriangle, Send } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  EMPTY_DATA, mergeWithDefault, MedicalHistoryFormFields,
} from "@/components/medical-history/MedicalHistoryTab";

// =====================================================================
// PublicMedicalHistory — patient-facing, unauthenticated form.
//
// URL shape: /medical-history/<token>
//
// Flow:
//   1. Read /:token from the route
//   2. Call public.get_medical_history_by_token(token) — anon-callable
//      RPC, returns clinic name, patient first name, blank form,
//      expiry. 401-style errors render as friendly copy.
//   3. Patient fills the form (the SAME field component the dentist
//      sees, minus the AlertsPanel and StatusBanner — those are
//      clinical analysis, not patient-facing).
//   4. Submit → public.submit_medical_history_via_token(token, data).
//      Token is single-use; success renders a thank-you screen.
// =====================================================================

export default function PublicMedicalHistory() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(null);   // { history_id, patient_first_name, clinic_name, ... }
  const [form, setForm] = useState(EMPTY_DATA);

  useEffect(() => {
    setLoading(true);
    supabase.rpc("get_medical_history_by_token", { p_token: token })
      .then(({ data, error: e }) => {
        setLoading(false);
        if (e) { setError(e.message ?? "This link is invalid or has expired."); return; }
        if (!data) { setError("This link is invalid or has expired."); return; }
        setMeta(data);
        setForm(mergeWithDefault(data.data || {}));
      });
  }, [token]);

  async function handleSubmit() {
    setSubmitting(true);
    const { error: e } = await supabase.rpc("submit_medical_history_via_token", {
      p_token: token,
      p_data:  form,
    });
    setSubmitting(false);
    if (e) { setError(e.message ?? "Could not submit."); return; }
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <header className="mb-5 flex items-center gap-3">
          {meta?.clinic_logo_url ? (
            <img
              src={meta.clinic_logo_url}
              alt={meta?.clinic_name || "Clinic"}
              className="w-10 h-10 rounded-xl object-contain bg-white border border-slate-200 p-1"
            />
          ) : null}
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-amber-600">
              Medical history form
            </p>
            <p className="text-sm text-slate-700">{meta?.clinic_name || "Your dental clinic"}</p>
          </div>
        </header>

        {loading ? (
          <Card>
            <div className="text-center py-10">
              <Loader2 className="w-5 h-5 mx-auto animate-spin text-amber-500" />
              <p className="mt-3 text-sm text-slate-500">Loading your form…</p>
            </div>
          </Card>
        ) : error ? (
          <Card>
            <div className="text-center py-8 px-4" data-testid="public-mh-error">
              <div className="w-12 h-12 rounded-full bg-rose-50 mx-auto flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-500" />
              </div>
              <h2 className="mt-3 text-base font-semibold text-slate-900">We couldn't open this form.</h2>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">{error}</p>
              <p className="mt-4 text-xs text-slate-400">
                Please ask {meta?.clinic_name || "your dental clinic"} to send you a fresh link.
              </p>
            </div>
          </Card>
        ) : submitted ? (
          <Card>
            <div className="text-center py-10 px-4" data-testid="public-mh-thanks">
              <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-slate-900">
                Thank you{meta?.patient_first_name ? `, ${meta.patient_first_name}` : ""}!
              </h2>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                Your medical history has been sent to {meta?.clinic_name || "your dental clinic"}.
                The dentist will review it before your visit.
              </p>
              <p className="mt-4 text-xs text-slate-400">You can close this window.</p>
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <div className="px-5 py-4">
                <h1 className="text-xl font-bold text-slate-900">
                  Hi{meta?.patient_first_name ? ` ${meta.patient_first_name}` : ""} 👋
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  Please fill out your medical history. It takes about 5 minutes and helps your dentist
                  treat you safely.
                </p>
                <p className="mt-2 text-xs text-slate-400">
                  Everything you submit is private to {meta?.clinic_name || "your dental clinic"}.
                </p>
              </div>
            </Card>

            <div className="mt-4">
              <MedicalHistoryFormFields form={form} setForm={setForm} editable={true} />
            </div>

            <div className="mt-4 sticky bottom-2 z-10">
              <Card>
                <div className="p-3 flex items-center justify-end gap-2">
                  <p className="text-xs text-slate-500 mr-auto pl-2">
                    By submitting, you confirm the information is accurate to the best of your knowledge.
                  </p>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="public-mh-submit"
                    className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Submit
                  </button>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
      {children}
    </div>
  );
}
